import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, MomentumBadge, StatusBadge, Loader } from "@/components/common";
import { TrendingUp, Sparkles, Upload, FileVideo, ArrowRight, Flame } from "lucide-react";

const heroImg = "https://images.pexels.com/photos/10325707/pexels-photo-10325707.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function Metric({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-surface border border-white/10 rounded-md p-6 relative overflow-hidden hover:border-white/20 transition-colors" data-testid={`metric-${label.toLowerCase().replace(/ /g,'-')}`}>
      <div className="flex items-start justify-between">
        <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">{label}</span>
        <Icon className="w-4 h-4 text-white/30" strokeWidth={1.5} />
      </div>
      <div className={`font-mono text-4xl lg:text-5xl tracking-tight font-light mt-4 ${accent}`}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [perf, setPerf] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
    api.get("/youtube/performance").then((r) => setPerf(r.data)).catch(() => setPerf({ videos: [] }));
  }, []);

  if (!stats) return <div className="p-6 lg:p-10"><Loader label="Loading control room" /></div>;

  return (
    <div className="p-6 lg:p-10 max-w-7xl">
      <PageHeader
        eyebrow="Live · Monetize Mode"
        title="Control Room"
        subtitle="Your command center for spotting trends before they peak and turning them into money-making videos."
        testid="dashboard-header"
        action={
          <button data-testid="cta-discover-trends" onClick={() => nav("/trends")}
            className="bg-brand hover:bg-brand-hover text-white font-medium rounded-md px-6 py-2.5 text-sm transition-colors flex items-center gap-2">
            <Flame className="w-4 h-4" /> Discover Trends
          </button>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 fade-up">
        <Metric label="Trends Tracked" value={stats.total_trends ?? 0} icon={TrendingUp} accent="text-white" />
        <Metric label="Video Packages" value={stats.total_packages ?? 0} icon={FileVideo} accent="text-white" />
        <Metric label="Ready to Ship" value={stats.ready ?? 0} icon={Sparkles} accent="text-signal-yellow" />
        <Metric label="Uploaded" value={stats.uploaded ?? 0} icon={Upload} accent="text-signal-green" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-surface border border-white/10 rounded-md p-6 fade-up">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-heading text-xl tracking-tight">Hot Trends</h3>
            <button onClick={() => nav("/trends")} data-testid="view-all-trends" className="text-xs text-white/40 hover:text-white font-mono flex items-center gap-1 transition-colors">
              VIEW ALL <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          {(stats.recent_trends || []).length === 0 ? (
            <div className="relative rounded-md overflow-hidden border border-white/10">
              <img src={heroImg} alt="trends" className="w-full h-44 object-cover opacity-50" />
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40">
                <p className="text-white/70 text-sm mb-3">No trends discovered yet</p>
                <button onClick={() => nav("/trends")} data-testid="empty-discover-btn" className="bg-brand hover:bg-brand-hover text-white rounded-md px-5 py-2 text-sm font-medium transition-colors">Run Trend Radar</button>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {stats.recent_trends.map((t) => (
                <div key={t.id} className="py-3 flex items-center justify-between gap-4 group cursor-pointer" onClick={() => nav("/trends")} data-testid={`dash-trend-${t.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate group-hover:text-brand transition-colors">{t.title}</div>
                    <div className="text-xs text-white/40 truncate font-mono mt-0.5">{t.predicted_growth} · {t.niche}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <MomentumBadge momentum={t.momentum} />
                    <span className="font-mono text-xl text-signal-green w-10 text-right">{t.trend_score}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-surface border border-white/10 rounded-md p-6 fade-up">
          <h3 className="font-heading text-xl tracking-tight mb-5">Recent Packages</h3>
          {(stats.recent_content || []).length === 0 ? (
            <p className="text-white/40 text-sm">Generate your first video package in the Content Studio.</p>
          ) : (
            <div className="space-y-3">
              {stats.recent_content.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 cursor-pointer group" onClick={() => nav("/library")} data-testid={`dash-pkg-${c.id}`}>
                  <div className="text-sm truncate group-hover:text-brand transition-colors">{(c.titles && c.titles[0]) || c.topic}</div>
                  <StatusBadge status={c.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {perf && perf.videos && perf.videos.length > 0 && (
        <div className="mt-4 bg-surface border border-white/10 rounded-md p-6 fade-up" data-testid="performance-panel">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-heading text-xl tracking-tight">Published Performance</h3>
            <div className="flex gap-5 text-right">
              <div><div className="font-mono text-xl text-white">{(perf.totals.views || 0).toLocaleString()}</div><div className="text-[9px] uppercase tracking-wider text-white/30 font-mono">views</div></div>
              <div><div className="font-mono text-xl text-signal-green">{(perf.totals.likes || 0).toLocaleString()}</div><div className="text-[9px] uppercase tracking-wider text-white/30 font-mono">likes</div></div>
              <div><div className="font-mono text-xl text-signal-blue">{(perf.totals.comments || 0).toLocaleString()}</div><div className="text-[9px] uppercase tracking-wider text-white/30 font-mono">comments</div></div>
            </div>
          </div>
          <div className="space-y-2">
            {perf.videos.map((v) => (
              <a key={v.id} href={v.url} target="_blank" rel="noopener noreferrer" data-testid={`perf-${v.id}`}
                className="flex items-center gap-4 bg-[#0A0A0A] border border-white/5 rounded px-3 py-2 hover:border-white/20 transition-colors">
                {v.thumbnail && <img src={v.thumbnail} alt="" className="w-16 h-10 object-cover rounded shrink-0" />}
                <span className="text-sm truncate flex-1">{v.title}</span>
                <span className="font-mono text-xs text-white/60 w-16 text-right">{v.views.toLocaleString()} 👁</span>
                <span className="font-mono text-xs text-signal-green w-14 text-right">{v.likes.toLocaleString()} ♥</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
